/** Provider accounts and provider configuration (EAS profile names). */
import { z } from 'zod';
import { ProviderKind, uuid } from './common.js';

/**
 * Who owns the provider account (`providerAccounts.managedBy`, DB enum `provider_managed_by`): `malible` = Malible owns
 * the Expo account / Bitrise workspace and manages it for the client (the default, and what every account is today);
 * `client` = reserved for a client that brings its own. Display and audit only: no behavior depends on it yet.
 */
export const ProviderManagedBy = z.enum(['malible', 'client']);
export type ProviderManagedBy = z.infer<typeof ProviderManagedBy>;

export const ProviderAccountCreateRequest = z.object({
  kind: ProviderKind,
  name: z.string().min(1).max(120),
  externalAccount: z.string().min(1).max(120),
  credentialId: uuid.optional(),
  concurrencyLimit: z.number().int().min(1).max(100).default(1),
  plan: z.string().max(120).optional(),
  managedBy: ProviderManagedBy.default('malible'),
  settings: z.record(z.string(), z.unknown()).default({}),
});
export type ProviderAccountCreateRequest = z.infer<typeof ProviderAccountCreateRequest>;

/**
 * An EAS build profile name: the `build.<name>` key of eas.json, which Hangar puts on the `eas build --profile <name>`
 * argv (SPEC §9.1). The console stores a Bitrise workflow id in the same `providerConfig.profile` field. Conservative on
 * purpose: letters, digits, `.`, `_` and `-`, starting with a letter or a digit (so it is never read as a flag), at most
 * 64 characters. Mirrored by `$defs/easProfileName` in schemas/hangar-config.schema.json.
 */
export const EAS_PROFILE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
export const EAS_PROFILE_NAME_RULE = 'letters, digits, ".", "_" and "-", starting with a letter or a digit, at most 64 characters';
export const isEasProfileName = (value: unknown): value is string => typeof value === 'string' && EAS_PROFILE_NAME_PATTERN.test(value);
export const easProfileName = z.string().regex(EAS_PROFILE_NAME_PATTERN, EAS_PROFILE_NAME_RULE);

/**
 * `providerConfig` of a build profile or of one of its environment bindings (eas: `{profile, resourceClass}`, bitrise:
 * `{workflowId, stack}`). The record stays open; the keys Hangar puts on a command line are validated: `profile`.
 */
export const ProviderConfig = z.looseObject({ profile: easProfileName.optional() });
export type ProviderConfig = z.infer<typeof ProviderConfig>;

/** `ProviderAccountPatch` (merge patch). */
export const ProviderAccountPatch = z.object({
  name: z.string().min(1).max(120).optional(),
  credentialId: uuid.nullable().optional(),
  concurrencyLimit: z.number().int().min(1).max(100).optional(),
  plan: z.string().max(120).nullable().optional(),
  status: z.enum(['active', 'disabled']).optional(),
  managedBy: ProviderManagedBy.optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
}).strict();
export type ProviderAccountPatch = z.infer<typeof ProviderAccountPatch>;

/**
 * `POST /provider-accounts/verify` (platform operators): what an Expo or Bitrise token reaches and whether it can build for
 * the chosen account, before Hangar stores it. Without `externalAccount` the answer lists the accounts (Expo) or
 * workspaces (Bitrise) the token sees; with one it also checks access (and, for Expo, the Developer permissions).
 */
export const ProviderTokenVerifyRequest = z.object({
  kind: ProviderKind,
  token: z.string().trim().min(20).max(4096),
  externalAccount: z.string().trim().min(1).max(120).optional(),
});
export type ProviderTokenVerifyRequest = z.infer<typeof ProviderTokenVerifyRequest>;

export const ProviderAccountTestRequest = z.object({ apply: z.boolean().default(false) });
export type ProviderAccountTestRequest = z.infer<typeof ProviderAccountTestRequest>;

/**
 * Malible's own provider tokens (SPEC decision 19), set once by a platform operator in Hangar administration → Expo &
 * Bitrise. A token is one opaque word: whitespace around it is trimmed, inside it is refused. Validation messages never
 * echo the value.
 */
const platformToken = z.string().trim().min(20, 'paste the whole token').max(4096).regex(/^\S+$/, 'a token has no spaces');

/** A Bitrise workspace slug: the id in its URL (app.bitrise.io/workspaces/<slug>), not its display name. */
export const BITRISE_WORKSPACE_PATTERN = /^[A-Za-z0-9_-]{1,120}$/;

/** `PUT /platform/providers/expo`: an Expo USER access token of an owner of Malible's Expo account. */
export const PlatformExpoTokenRequest = z.object({ token: platformToken });
export type PlatformExpoTokenRequest = z.infer<typeof PlatformExpoTokenRequest>;

/** `PUT /platform/providers/bitrise`: Malible's Bitrise token and the workspace every client's builds run in. */
export const PlatformBitriseTokenRequest = z.object({
  token: platformToken,
  workspace: z.string().trim().regex(BITRISE_WORKSPACE_PATTERN, 'the workspace slug from its URL (letters, digits, "-" and "_"), not its name'),
});
export type PlatformBitriseTokenRequest = z.infer<typeof PlatformBitriseTokenRequest>;
